import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { logUnresolvedS3Key, resolveDocumentS3Keys } from "@/lib/order-doc-delete";

/**
 * Best-effort cleanup of BoL artifacts when an order is canceled.
 *
 * For every `order_bol_images` row attached to the canceled order:
 *   1. Delete the file from the per-tenant NetSuite-facing S3 bucket via
 *      `sync-bol-s3` `action: "delete"` (sync-then-persist).
 *   2. Best-effort remove the underlying object from the `bol-images`
 *      storage bucket.
 *   3. Delete the `order_bol_images` row.
 *
 * Failures NEVER block the cancellation — they surface as toast warnings.
 * S3 metadata is handled separately by the `enqueue_s3_metadata_change`
 * trigger, which automatically enqueues a `delete` operation to the
 * `s3_metadata_outbox` poller on status → 'canceled'.
 *
 * Pre-prod tenants (no S3 bucket) are skipped server-side by `sync-bol-s3`,
 * so this helper is safe to call in every environment.
 */
export async function removeOrderBolsOnCancel(params: {
  orderId: string;
  orderUniqueId: string;
  tenantId: string;
  tenantSlug: string | null | undefined;
}): Promise<void> {
  const { orderId, orderUniqueId, tenantId, tenantSlug } = params;
  if (!tenantSlug || !tenantId || !orderId) return;

  try {
    // 1. Find every BoL row attached to this order (both direct uploads
    //    AND trailer-sourced rows — cancellation discards both).
    const { data: bols, error: lookupErr } = await supabase
      .from("order_bol_images")
      .select("id, image_path, s3_key, file_name")
      .eq("order_id", orderId)
      .eq("tenant_id", tenantId);
    if (lookupErr) {
      console.warn("removeOrderBolsOnCancel: lookup failed:", lookupErr.message);
      toast.warning(`BoL cleanup warning: ${lookupErr.message}`);
      return;
    }
    if (!bols || bols.length === 0) return;

    const filePaths = bols
      .map((b: any) => b.image_path)
      .filter((p): p is string => !!p);

    // 2/3. Resolve each row's S3 key through the shared resolver (row `s3_key`
    //      first, then the sync log by ORIGINAL file name) and issue the delete.
    //      A key that cannot be resolved is logged as a failure — never skipped
    //      silently, which used to leave objects orphaned in the bucket.
    let s3DeleteErrors = 0;
    let unresolved = 0;
    for (const bol of bols as any[]) {
      const keys = await resolveDocumentS3Keys(bol, orderUniqueId);
      if (keys.length === 0) {
        unresolved += 1;
        console.warn(
          `[bol-cancellation-cleanup] No S3 key resolved for order_bol_images ${bol.id} (order ${orderUniqueId})`,
        );
        await logUnresolvedS3Key({
          bucket: "bol-images",
          orderUniqueId,
          tenantId,
          sourcePath: bol.file_name ?? bol.image_path ?? null,
          context: "order cancellation cleanup",
        });
        continue;
      }
      for (const key of keys) {
        const { data, error } = await supabase.functions.invoke("sync-bol-s3", {
          body: {
            action: "delete",
            tenant_slug: tenantSlug,
            order_id: orderUniqueId,
            s3_key: key,
          },
        });
        if (error || (data && data.success === false)) {
          s3DeleteErrors += 1;
          console.warn(
            "sync-bol-s3 delete failed for",
            key,
            error?.message ?? data?.error,
          );
        }
      }
    }

    // 4. Best-effort: remove underlying storage objects from bol-images bucket.
    if (filePaths.length > 0) {
      await supabase.storage.from("bol-images").remove(filePaths).catch(() => undefined);
    }

    // 5. Delete the order_bol_images rows regardless of S3 outcome.
    const ids = bols.map((b) => b.id);
    const { error: delErr } = await supabase
      .from("order_bol_images")
      .delete()
      .in("id", ids)
      .eq("tenant_id", tenantId);
    if (delErr) {
      console.warn("order_bol_images cleanup failed:", delErr.message);
      toast.warning(`BoL local cleanup warning: ${delErr.message}`);
    }

    if (s3DeleteErrors > 0 || unresolved > 0) {
      const parts: string[] = [];
      if (s3DeleteErrors > 0) parts.push(`${s3DeleteErrors} S3 deletion${s3DeleteErrors === 1 ? "" : "s"} failed`);
      if (unresolved > 0) parts.push(`${unresolved} with no resolvable S3 key`);
      toast.warning(
        `BoL cleanup for canceled order: ${parts.join(", ")}. See console for details.`,
      );
    } else if (ids.length > 0) {
      toast.success(
        `Removed ${ids.length} BoL${ids.length === 1 ? "" : "s"} from canceled order`,
      );
    }
  } catch (err: any) {
    console.warn("removeOrderBolsOnCancel threw:", err);
    toast.warning(`BoL cleanup warning: ${err?.message ?? "unknown error"}`);
  }
}